import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Heart, HandHeart, Sparkles, GraduationCap, TreePine, Users, LifeBuoy, Stethoscope, Milk } from 'lucide-react';
import { api, pickLocale } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

const CATS = ['all', 'elderly-care', 'animal-welfare', 'cow-care', 'healthcare', 'education', 'environment', 'community', 'disaster-relief'];
const PILLAR_ICONS = {
  'elderly-care': HandHeart, 'animal-welfare': Heart, 'cow-care': Milk, 'healthcare': Stethoscope,
  'education': GraduationCap, 'environment': TreePine, 'community': Users, 'disaster-relief': LifeBuoy,
};

export default function Projects() {
  const { t, i18n } = useTranslation();
  useSEO({ title: 'Programmes', description: 'Explore the flagship programmes of Universal Krishna Gaukul Mission across elderly care, cow care, animal welfare, healthcare, education, environment and disaster relief.' });
  const lang = i18n.language.split('-')[0];
  const [cat, setCat] = useState('all');
  const { data: items = [], isLoading } = useQuery({ queryKey: ['projects'], queryFn: async () => (await api.get('/projects')).data });
  const filtered = cat === 'all' ? items : items.filter(p => p.category === cat);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="projects-page">
      <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('projects.eyebrow')}</div>
      <h1 className="mt-3 font-serif text-4xl md:text-6xl text-ukgm-blue font-semibold leading-tight">{t('projects.title')}</h1>
      <p className="mt-4 max-w-2xl text-ukgm-slate text-lg">{t('projects.subtitle')}</p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="project-filters">
        {CATS.map(c => (
          <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border ${cat === c ? 'bg-ukgm-blue text-white border-ukgm-blue' : 'border-ukgm-gold/40 text-ukgm-blue hover:bg-ukgm-goldLight'}`}>
            {t(`projects.categories.${c}`)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="mt-10 text-ukgm-slate">{t('common.loading')}</div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(p => {
            const Icon = PILLAR_ICONS[p.category] || Sparkles;
            return (
              <Link key={p.slug} to={`/projects/${p.slug}`} className="group bg-white rounded-2xl border border-ukgm-gold/20 p-6 card-lift" data-testid={`project-item-${p.slug}`}>
                <div className="w-10 h-10 rounded-full bg-ukgm-blue text-ukgm-gold flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="mt-4 text-[10px] tracking-widest uppercase text-ukgm-gold font-bold">{t(`projects.categories.${p.category}`)}</div>
                <h3 className="mt-2 font-serif text-xl text-ukgm-blue font-semibold group-hover:text-ukgm-gold leading-snug">{pickLocale(p.title, lang)}</h3>
                <p className="mt-3 text-sm text-ukgm-slate leading-relaxed line-clamp-4">{pickLocale(p.summary, lang)}</p>
                <div className="mt-5 text-xs text-ukgm-gold font-semibold inline-flex items-center gap-1">{t('projects.view')} <ArrowRight className="w-3.5 h-3.5" /></div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
